// src/features/kommunikation/components/DocumentList.tsx

import React, { useState } from 'react';
import {
  Clock, CheckCircle2, XCircle, Ban, FileText, AlertTriangle,
  ChevronDown, ChevronRight, Link2, Copy, Eye, EyeOff,
  Send, FlaskConical, Paperclip, Trash2, Loader2, Inbox, FileCheck
} from 'lucide-react';
import { DocumentWithCredentials } from '../types';
import { documentParserService } from '../services/DocumentParserService';
import { ConfirmModal } from './ConfirmModal';
import { ReplaceDocumentModal } from './ReplaceDocumentModal';

interface DocumentListProps {
  documents: DocumentWithCredentials[];
  onDelete: (documentId: string) => Promise<boolean>;
  onStatusUpdate: (documentId: string, status: 'geplant' | 'versendet' | 'fehler' | 'abgebrochen') => Promise<boolean>;
  onSendNow: (documentId: string) => Promise<boolean>;
  onTestSend: (documentId: string) => Promise<boolean>;
  onReplace: (documentId: string, file: File) => Promise<boolean>;
  onGenerateProof: (documentId: string) => Promise<boolean>;
}

// Bug-Fix: actionLoading speichert jetzt ID UND Aktion
interface ActionLoadingState {
  id: string;
  action: 'send' | 'delete' | 'status' | 'testSend' | 'replace' | 'proof';
}

// Status-Konfiguration: Farben + Lucide-Icon je Versandstatus
const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string; Icon: React.ComponentType<{ size?: number; color?: string }> }> = {
  geplant: { label: 'Geplant', bg: '#FEF3C7', color: '#92400E', Icon: Clock },
  versendet: { label: 'Versendet', bg: '#D1FAE5', color: '#065F46', Icon: CheckCircle2 },
  fehler: { label: 'Fehler', bg: '#FEE2E2', color: '#991B1B', Icon: XCircle },
  abgebrochen: { label: 'Abgebrochen', bg: '#E5E7EB', color: '#374151', Icon: Ban }
};

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  onDelete,
  onStatusUpdate,
  onSendNow,
  onTestSend,
  onReplace,
  onGenerateProof
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<ActionLoadingState | null>(null);
  
  // State für Passwort-Sichtbarkeit (speichert IDs der Dokumente, bei denen das Passwort sichtbar ist)
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());
  
  // Confirm Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmButtonColor?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmButtonColor: '#EF4444',
    onConfirm: () => {}
  });

  // Replace Modal State
  const [replaceModal, setReplaceModal] = useState<{
    isOpen: boolean;
    doc: DocumentWithCredentials | null;
  }>({
    isOpen: false,
    doc: null
  });

  const getStatusBadge = (status: string) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.geplant;
    const Icon = cfg.Icon;

    return (
      <span
        style={{
          padding: '4px 10px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: cfg.bg,
          color: cfg.color,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px'
        }}
      >
        <Icon size={13} color={cfg.color} />
        {cfg.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper: Prüft ob eine bestimmte Aktion für ein Dokument läuft
  const isActionLoading = (docId: string, action?: ActionLoadingState['action']) => {
    if (!actionLoading) return false;
    if (actionLoading.id !== docId) return false;
    if (action && actionLoading.action !== action) return false;
    return true;
  };

  // Helper: Prüft ob irgendeine Aktion für ein Dokument läuft
  const isAnyActionLoading = (docId: string) => {
    return actionLoading?.id === docId;
  };

  // Helper: Passwort-Sichtbarkeit umschalten
  const togglePasswordVisibility = (docId: string) => {
    setVisiblePasswords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  // Helper: Link in Zwischenablage kopieren
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Kopieren fehlgeschlagen:', err);
    }
  };

  const handleDeleteClick = (documentId: string, filename: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Dokument löschen',
      message: `Möchten Sie das Dokument "${filename}" wirklich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden.`,
      confirmButtonColor: '#EF4444',
      onConfirm: () => executeDelete(documentId)
    });
  };

  const executeDelete = async (documentId: string) => {
    setConfirmModal({ ...confirmModal, isOpen: false });
    setActionLoading({ id: documentId, action: 'delete' });
    try {
      await onDelete(documentId);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusChangeClick = (documentId: string, status: 'abgebrochen', filename: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Versand abbrechen',
      message: `Möchten Sie den Versand des Dokuments "${filename}" wirklich abbrechen?`,
      confirmButtonColor: '#F59E0B',
      onConfirm: () => executeStatusChange(documentId, status)
    });
  };

  const executeStatusChange = async (documentId: string, status: 'abgebrochen') => {
    setConfirmModal({ ...confirmModal, isOpen: false });
    setActionLoading({ id: documentId, action: 'status' });
    try {
      await onStatusUpdate(documentId, status);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handler für sofortigen Versand
   */
  const handleSendNowClick = (documentId: string, filename: string, isResend: boolean) => {
    const title = isResend ? 'Erneut senden' : 'Jetzt senden';
    const message = isResend
      ? `Möchten Sie das Dokument "${filename}" erneut an den Empfänger senden?`
      : `Möchten Sie das Dokument "${filename}" jetzt sofort senden (ohne auf den geplanten Zeitpunkt zu warten)?`;
    
    setConfirmModal({
      isOpen: true,
      title,
      message,
      confirmButtonColor: '#059669',
      onConfirm: () => executeSendNow(documentId)
    });
  };

  const executeSendNow = async (documentId: string) => {
    setConfirmModal({ ...confirmModal, isOpen: false });
    setActionLoading({ id: documentId, action: 'send' });
    try {
      await onSendNow(documentId);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Handler für Testversand an lohn@xtax.de
   */
  const handleTestSendClick = (documentId: string, filename: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Testversand',
      message: `Möchten Sie das Dokument "${filename}" als Test an lohn@xtax.de senden?\n\nDer Betreff wird mit [TEST] markiert. Der Dokument-Status bleibt unverändert.`,
      confirmButtonColor: '#8B5CF6',
      onConfirm: () => executeTestSend(documentId)
    });
  };

  const executeTestSend = async (documentId: string) => {
    setConfirmModal({ ...confirmModal, isOpen: false });
    setActionLoading({ id: documentId, action: 'testSend' });
    try {
      await onTestSend(documentId);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * NEU: Handler für Versandnachweis (PDF)
   * Rein lesend, kein Confirm noetig. Der Download startet direkt.
   */
  const executeGenerateProof = async (documentId: string) => {
    setActionLoading({ id: documentId, action: 'proof' });
    try {
      await onGenerateProof(documentId);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * NEU: Handler für PDF-Austausch
   * Öffnet das ReplaceDocumentModal mit dem gewählten Dokument.
   */
  const handleReplaceClick = (doc: DocumentWithCredentials) => {
    setReplaceModal({ isOpen: true, doc });
  };

  /**
   * Wird vom ReplaceDocumentModal nach Datei-Auswahl aufgerufen.
   * Setzt das ActionLoading und delegiert den Austausch an den Hook.
   */
  const handleReplaceSubmit = async (documentId: string, file: File): Promise<boolean> => {
    setActionLoading({ id: documentId, action: 'replace' });
    try {
      return await onReplace(documentId, file);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReplaceClose = () => {
    setReplaceModal({ isOpen: false, doc: null });
  };

  /**
   * Bestimmt den Button-Text basierend auf Status
   */
  const getSendButtonText = (status: string): string => {
    switch (status) {
      case 'geplant':
        return 'Jetzt senden';
      case 'versendet':
        return 'Erneut senden';
      case 'fehler':
        return 'Erneut versuchen';
      default:
        return 'Senden';
    }
  };

  if (documents.length === 0) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: '#6B7280' }}>
        <Inbox size={40} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: '15px', fontWeight: '500' }}>Keine Dokumente vorhanden</div>
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
        Dokumente ({documents.length})
      </h3>

      {documents.map((doc) => (
        <div
          key={doc.id}
          style={{
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
            marginBottom: '12px',
            backgroundColor: 'white',
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <div
            onClick={() => setExpandedId(expandedId === doc.id ? null : doc.id)}
            style={{
              padding: '16px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: expandedId === doc.id ? '#F9FAFB' : 'white'
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                  {doc.original_filename}
                </span>
                {getStatusBadge(doc.versand_status)}
                {doc.ist_sonderversand && (
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: '#EDE9FE',
                      color: '#5B21B6',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <FileText size={12} color="#5B21B6" /> Sonderversand
                  </span>
                )}
                {!doc.hasCredential && (
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: '#FEE2E2',
                      color: '#991B1B',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <AlertTriangle size={12} color="#991B1B" /> Keine Credentials
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                Mandant: {doc.mandantennummer} | Personal: {doc.personalnummer}
                {!doc.ist_sonderversand && doc.monat != null && doc.jahr != null && (
                  <> | {documentParserService.formatMonthYear(doc.monat, doc.jahr)}</>
                )}
                {doc.beraternummer && ` | Berater: ${doc.beraternummer}`}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', color: '#9CA3AF', flexShrink: 0 }}>
              {expandedId === doc.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
          </div>

          {/* Expanded Details */}
          {expandedId === doc.id && (
            <div style={{ padding: '16px', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                    Geplanter Versand
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {formatDate(doc.versand_datum)}
                  </div>
                </div>
                {doc.versendet_am && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                      Versendet am
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                      {formatDate(doc.versendet_am)}
                    </div>
                  </div>
                )}
                {doc.credential && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                      E-Mail-Empfänger
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                      {doc.credential.email}
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                    Dateigröße
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                    Hochgeladen von
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {doc.uploaded_by_email}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                    Token gültig bis
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {formatDate(doc.token_ablaufdatum)}
                  </div>
                </div>
              </div>

              {/* Download-Link und Passwort Anzeige */}
              <div
                style={{
                  padding: '12px',
                  backgroundColor: '#F0FDF4',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid #BBF7D0'
                }}
              >
                <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Link2 size={14} color="#166534" /> Download-Zugang
                </div>
                
                {/* Download-Link */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>
                    Link:
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code
                      style={{
                        fontSize: '12px',
                        backgroundColor: 'white',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        border: '1px solid #D1D5DB',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      https://secure.xtax.de/dokument/{doc.zugriffs_token}
                    </code>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(`https://secure.xtax.de/dokument/${doc.zugriffs_token}`);
                      }}
                      style={{
                        padding: '6px 8px',
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="Link kopieren"
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
                
                {/* Passwort */}
                {doc.credential?.passwort && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>
                      Passwort:
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <code
                        style={{
                          fontSize: '14px',
                          backgroundColor: 'white',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: '1px solid #D1D5DB',
                          fontFamily: 'monospace',
                          letterSpacing: visiblePasswords.has(doc.id) ? '2px' : '0px'
                        }}
                      >
                        {visiblePasswords.has(doc.id) ? doc.credential.passwort : '••••'}
                      </code>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePasswordVisibility(doc.id);
                        }}
                        style={{
                          padding: '6px 8px',
                          backgroundColor: '#6B7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title={visiblePasswords.has(doc.id) ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      >
                        {visiblePasswords.has(doc.id) ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(doc.credential!.passwort);
                        }}
                        style={{
                          padding: '6px 8px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Passwort kopieren"
                      >
                        <Copy size={13} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {doc.fehler_nachricht && (
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#FEE2E2',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#991B1B'
                  }}
                >
                  <strong>Fehler:</strong> {doc.fehler_nachricht}
                </div>
              )}

              {doc.zugegriffen_am && (
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#DBEAFE',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#1E40AF'
                  }}
                >
                  <strong>Zugriff:</strong> {formatDate(doc.zugegriffen_am)} ({doc.zugriffs_count}x)
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {/* Jetzt senden / Erneut senden Button */}
                {doc.hasCredential && doc.versand_status !== 'abgebrochen' && (
                  <button
                    onClick={() => handleSendNowClick(
                      doc.id, 
                      doc.original_filename, 
                      doc.versand_status === 'versendet' || doc.versand_status === 'fehler'
                    )}
                    disabled={isAnyActionLoading(doc.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isAnyActionLoading(doc.id) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: isAnyActionLoading(doc.id) ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isActionLoading(doc.id, 'send')
                      ? <><Loader2 size={15} /> Wird gesendet...</>
                      : <><Send size={15} /> {getSendButtonText(doc.versand_status)}</>}
                  </button>
                )}

                {/* Testversand Button */}
                {doc.hasCredential && doc.versand_status !== 'abgebrochen' && (
                  <button
                    onClick={() => handleTestSendClick(doc.id, doc.original_filename)}
                    disabled={isAnyActionLoading(doc.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#8B5CF6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isAnyActionLoading(doc.id) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: isAnyActionLoading(doc.id) ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isActionLoading(doc.id, 'testSend')
                      ? <><Loader2 size={15} /> Testversand...</>
                      : <><FlaskConical size={15} /> Testversand</>}
                  </button>
                )}

                {/* NEU: Versandnachweis erzeugen - nur bei versendeten Dokumenten */}
                {doc.versand_status === 'versendet' && (
                  <button
                    onClick={() => executeGenerateProof(doc.id)}
                    disabled={isAnyActionLoading(doc.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#0D9488',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isAnyActionLoading(doc.id) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: isAnyActionLoading(doc.id) ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isActionLoading(doc.id, 'proof')
                      ? <><Loader2 size={15} /> Nachweis wird erstellt...</>
                      : <><FileCheck size={15} /> Versandnachweis</>}
                  </button>
                )}

                {/* NEU: PDF austauschen Button - bei allen Status ausser abgebrochen */}
                {doc.versand_status !== 'abgebrochen' && (
                  <button
                    onClick={() => handleReplaceClick(doc)}
                    disabled={isAnyActionLoading(doc.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#0EA5E9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isAnyActionLoading(doc.id) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: isAnyActionLoading(doc.id) ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isActionLoading(doc.id, 'replace')
                      ? <><Loader2 size={15} /> Wird ausgetauscht...</>
                      : <><Paperclip size={15} /> PDF austauschen</>}
                  </button>
                )}

                {/* Versand abbrechen Button - nur bei geplant */}
                {doc.versand_status === 'geplant' && (
                  <button
                    onClick={() => handleStatusChangeClick(doc.id, 'abgebrochen', doc.original_filename)}
                    disabled={isAnyActionLoading(doc.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#F59E0B',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isAnyActionLoading(doc.id) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isActionLoading(doc.id, 'status')
                      ? <><Loader2 size={15} /> Wird abgebrochen...</>
                      : <><Ban size={15} /> Versand abbrechen</>}
                  </button>
                )}

                {/* Löschen Button */}
                <button
                  onClick={() => handleDeleteClick(doc.id, doc.original_filename)}
                  disabled={isAnyActionLoading(doc.id)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#EF4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isAnyActionLoading(doc.id) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {isActionLoading(doc.id, 'delete')
                    ? <><Loader2 size={15} /> Wird gelöscht...</>
                    : <><Trash2 size={15} /> Löschen</>}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Ja, fortfahren"
        cancelText="Abbrechen"
        confirmButtonColor={confirmModal.confirmButtonColor || '#EF4444'}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Replace Modal */}
      <ReplaceDocumentModal
        isOpen={replaceModal.isOpen}
        doc={replaceModal.doc}
        onClose={handleReplaceClose}
        onReplace={handleReplaceSubmit}
      />
    </div>
  );
};
